import {
  Explanation,
  ExplanationRow,
  Game,
} from "../../../services/gameQuiz/gameQuiz.types";

export const generateGameId = (): string =>
  `g${Date.now()}${Math.random().toString(36).substring(2, 11)}`;

export const generateOptionId = (): string =>
  Math.random().toString(36).substring(2, 11);

export const generateRowId = (): string =>
  `r${Date.now()}${Math.random().toString(36).substring(2, 9)}`;

export const createEmptyExplanationRow = (): ExplanationRow => ({
  id: generateRowId(),
  text: "",
  image: "",
});

export const createEmptyExplanation = (): Explanation => ({
  answer: "",
  audio: "",
  rows: [createEmptyExplanationRow()],
});

type TextSelectionPart = Extract<
  Game,
  { type: "text_selection" }
>["parts"][number];

// Divide la frase en palabras (seleccionables) y conserva el espacio/puntuación
// posterior a cada palabra en `trailing`, para reconstruir la frase intacta.
// Vive acá (y no en el editor) porque también la usa el importador YAML.
export const tokenizeWords = (text: string): TextSelectionPart[] => {
  const parts: TextSelectionPart[] = [];
  // Captura: secuencias de caracteres "de palabra" (letras/números/_) como tokens
  // seleccionables; todo lo demás (espacios, puntuación) es pegamento.
  const regex = /([\p{L}\p{N}_]+)|([^\p{L}\p{N}_]+)/gu;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    const word = m[1];
    const glue = m[2];
    if (word) {
      parts.push({
        id: generateOptionId(),
        text: word,
        selectable: true,
        correct: false,
        trailing: "",
      });
    } else if (glue) {
      // El pegamento va como `trailing` de la última palabra; si no hay palabra
      // previa (la frase empieza con puntuación/espacio), va como parte fija.
      const last = parts[parts.length - 1];
      if (last && last.selectable) {
        last.trailing = (last.trailing ?? "") + glue;
      } else {
        parts.push({
          id: generateOptionId(),
          text: glue,
          selectable: false,
          correct: false,
        });
      }
    }
  }

  return parts;
};

export const createEmptyGame = (type: Game["type"]): Game => {
  const id = generateGameId();
  switch (type) {
    case "multiple_choice":
      return {
        id,
        type: "multiple_choice",
        question: "",
        options: [
          { id: generateOptionId(), text: "", correct: false },
          { id: generateOptionId(), text: "", correct: false },
        ],
      };
    case "true_false": {
      const trueId = generateOptionId();
      const falseId = generateOptionId();
      return {
        id,
        type: "true_false",
        question: "",
        categories: [
          { id: trueId, label: "Verdadero" },
          { id: falseId, label: "Falso" },
        ],
        statements: [
          { id: generateOptionId(), text: "", correctCategoryId: trueId },
        ],
      };
    }
    case "drag_and_drop":
      return {
        id,
        type: "drag_and_drop",
        question: "",
        items: [
          { id: generateOptionId(), text: "" },
          { id: generateOptionId(), text: "" },
        ],
      };
    case "drag_and_drop_horizontal":
      return {
        id,
        type: "drag_and_drop_horizontal",
        question: "",
        items: [
          { id: generateOptionId(), text: "" },
          { id: generateOptionId(), text: "" },
        ],
      };
    case "fill_in_blanks":
      return {
        id,
        type: "fill_in_blanks",
        question: "",
        text: "",
        blanks: [],
        options: [],
      };
    case "categorization":
      return {
        id,
        type: "categorization",
        question: "",
        categories: [],
        items: [],
        distractors: [],
      };
    case "matching":
      return {
        id,
        type: "matching",
        question: "",
        pairs: [
          {
            id: generateOptionId(),
            left: { text: "", image: "" },
            right: { text: "", image: "" },
          },
        ],
      };
    case "text_input":
      return {
        id,
        type: "text_input",
        question: "",
        correctAnswers: [{ id: generateOptionId(), text: "" }],
      };
    case "text_input_ai":
      return {
        id,
        type: "text_input_ai",
        question: "",
        modelAnswer: "",
        keyIdeas: "",
        keywords: "",
        subject: "",
        grade: "",
      };
    case "image_labeling":
      return {
        id,
        type: "image_labeling",
        question: "",
        image: "",
        hotspots: [],
        labels: [],
      };
    case "image_hotspot":
      return {
        id,
        type: "image_hotspot",
        question: "",
        image: "",
        hotspots: [],
      };
    case "text_selection":
      return {
        id,
        type: "text_selection",
        question: "",
        text: "",
        authoringMode: "words",
        parts: [],
      };
  }
};

export const GAME_TYPE_LABELS: Record<Game["type"], string> = {
  multiple_choice: "Opción Múltiple",
  true_false: "Verdadero/Falso",
  drag_and_drop: "Secuenciar vertical",
  drag_and_drop_horizontal: "Secuenciar horizontal",
  fill_in_blanks: "Completar Espacios",
  categorization: "Categorización",
  matching: "Emparejar",
  text_input: "Texto Corto",
  text_input_ai: "Texto Corto IA",
  image_labeling: "Etiquetar Imagen",
  image_hotspot: "Seleccionar Área",
  text_selection: "Seleccionar Texto",
};
